import { all, call, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import * as actions from './actions';
import { getErrorMessage } from '../../api/network';
import { apiCall } from '../common/operations';
import { courseActions } from '../course/index';
import { taskApi } from 'shovel-lib';
import { calendarActions } from '../calendar';
import { icsActions } from '../ics';
import { termActions } from '../terms';
import storage from '@utils/storage';
import { GroupByField } from '@components/course/types';
import { getSelectedItemInfo } from '@state/workload/selectors';
import { SidebarItemType } from '@state/workload/types';
import { tasksUndone } from '../undo/actions';
import { closeManageTaskDialog } from '../task/actions';
import { record as recordUndo } from '../undo/actions';
import { getId } from '../semester/selectors';
import { moveInTaskList } from '../../api/taskListOrderApi';
import { actionIdOf } from '../../api/activityLogApi';
import t from '../../i18n/t';

function* getWorkload(action: ReturnType<typeof actions.getWorkload.request>) {
  const { ok, ...response } = yield apiCall(taskApi.getWorkload, action.payload);
  if (ok) {
    yield put(actions.getWorkload.success(response.data));
  } else {
    yield put(actions.getWorkload.failure(getErrorMessage(response)));
  }
}

// The Task List's Planned column changes when sessions are planned, moved or removed on the calendar or in the task
// popup, so the list is fetched again, quietly (no skeleton), when it opens, after an Undo, and when the task popup
// closes on it.
function* refreshWorkload() {
  const { ok, ...response } = yield apiCall(taskApi.getWorkload, undefined);
  if (ok) yield put(actions.getWorkload.success(response.data));
}

function* refreshWorkloadAfterTaskPopup() {
  if (window.location.pathname.endsWith('/tasks')) yield call(refreshWorkload);
}

function* selectCommitment() {
  yield delay(200); // force skeleton
  yield put(actions.selectCommitmentSuccess());
}

function* onStartCourseWorkloadListener() {
  while (true) {
    const { type, payload } = yield take([
      courseActions.deleteTaskCategory.success,
      courseActions.deleteReadingSource.success,
      courseActions.updateReadingSource.success,
      courseActions.updateTaskCategory.success,
      calendarActions.deleteCourse.success,
      calendarActions.deleteActivity.success,
      courseActions.updateCalendarId.success,
      courseActions.importCourse.success,
      icsActions.triggerIcsUpdates.success,
      icsActions.unsyncCourseFromLMS.success,
      termActions.createBatchCourses.success,
      courseActions.reorderCategoriesInsideCourse.success,
      actions.stopCourseWorkloadListener
    ]);

    if (type === getType(actions.stopCourseWorkloadListener)) {
      break;
    }

    if (
      // don't refresh if course/activity is deleted and mode is not ViewEverything
      (type === getType(calendarActions.deleteCourse.success) ||
        type === getType(calendarActions.deleteActivity.success) ||
        type === getType(courseActions.deleteTaskCategory.success)) &&
      !payload.refreshTaskList
    ) {
      continue;
    }

    const groupBy = yield call(storage.getGroupWorkloadBy);
    // don't refresh after reorder of task categories if group by is not by category
    // or selected course is not the one of reordered categories
    if (type === getType(courseActions.reorderCategoriesInsideCourse.success)) {
      const { courseId } = payload;
      const { selectedItemId, selectedItemType } = yield select(getSelectedItemInfo);
      if (
        groupBy !== GroupByField.CATEGORY ||
        !(courseId === selectedItemId && selectedItemType === SidebarItemType.COURSE)
      ) {
        continue;
      }
    }

    // Edits made from a picker (a source's time per page, a category's name, deleting or reordering them) refresh
    // quietly: the skeleton would close the add row open behind the picker and lose the task being written.
    if (QUIET.includes(type)) {
      yield call(refreshWorkload);
    } else {
      yield put(actions.getWorkload.request(undefined));
    }
  }
}

const QUIET = [
  courseActions.deleteTaskCategory.success,
  courseActions.deleteReadingSource.success,
  courseActions.updateReadingSource.success,
  courseActions.updateTaskCategory.success,
  courseActions.reorderCategoriesInsideCourse.success
].map(a => getType(a));

// "Sort: By hand": saved after the row has moved. Should the save fail, the list is fetched again, which puts it back.
function* moveTaskInList({ payload }: ReturnType<typeof actions.moveTaskInList.request>) {
  const semesterId: number = yield select(getId);
  const { ok, ...response } = yield apiCall(moveInTaskList, semesterId, payload.move);
  if (!ok) {
    yield put(actions.moveTaskInList.failure(getErrorMessage(response)));
    yield put(actions.refreshWorkload());
    return;
  }
  const orders: { id: number; listOrder: number }[] = response.data || [];
  yield put(actions.moveTaskInList.success(orders));
  if (payload.was) {
    const { taskId } = payload.move;
    // The number it was given, for Undo to tell it hasn't been moved again since.
    const order = orders.find(({ id }) => id === taskId)?.listOrder;
    yield put(
      recordUndo({
        entry: { kind: 'listOrder', taskId, ...payload.was, order, actionId: actionIdOf(response) },
        message: t.TASK_MOVED
      })
    );
  }
}

export default function*() {
  yield all([
    takeLatest(actions.getWorkload.request, getWorkload),
    // Every move is saved, in order: a quick second drag must not cancel the first.
    takeEvery(actions.moveTaskInList.request, moveTaskInList),
    takeLatest([actions.refreshWorkload, tasksUndone], refreshWorkload),
    takeLatest(closeManageTaskDialog, refreshWorkloadAfterTaskPopup),
    takeLatest(actions.selectCommitment, selectCommitment),
    takeLatest(actions.startCourseWorkloadListener, onStartCourseWorkloadListener)
  ]);
}
